import React from 'react';
import { Link } from 'react-router-dom';

/**
 * AI IMAGE PROMPT FOR ai-technology-showcase.jpg:
 * "Futuristic AI neural network with glowing nodes and connections, 
 * blue and purple gradient colors, brain-shaped network structure, 
 * holographic educational symbols (books, formulas, atoms) integrated, 
 * circuit board patterns, technology meets education, 8K quality, modern and inspiring"
 */

interface TechFeature {
  title: string;
  description: string;
  icon: string;
  color: string;
}

const AITechnologyShowcase: React.FC = () => {
  const techFeatures: TechFeature[] = [
    {
      title: "Machine Learning Analysis",
      description: "Our AI analyzes your performance patterns using advanced machine learning algorithms to identify strengths, weaknesses, and learning trends.",
      icon: "fas fa-brain",
      color: "#667eea"
    },
    {
      title: "Natural Language Processing",
      description: "Powered by GPT-4, our AI tutor understands your questions in natural language and provides contextually relevant explanations.",
      icon: "fas fa-comments",
      color: "#764ba2"
    },
    {
      title: "Adaptive Learning Engine",
      description: "Questions and difficulty levels automatically adapt based on your real-time performance, ensuring optimal challenge and growth.",
      icon: "fas fa-sync-alt",
      color: "#2d9cdb"
    },
    {
      title: "Predictive Analytics",
      description: "AI forecasts your exam readiness and predicts potential scores based on historical performance data and learning velocity.",
      icon: "fas fa-chart-pie",
      color: "#27ae60"
    },
    {
      title: "Computer Vision",
      description: "Extract questions from images and handwritten notes using advanced OCR and image recognition technology.",
      icon: "fas fa-eye",
      color: "#f39c12"
    },
    {
      title: "Personalization Engine",
      description: "Every student gets a unique experience tailored to their learning style, pace, goals, and knowledge gaps.",
      icon: "fas fa-user-cog",
      color: "#e74c3c"
    }
  ];

  const aiCapabilities = [
    {
      stat: "100+",
      label: "Performance Metrics",
      icon: "fas fa-tachometer-alt"
    },
    {
      stat: "Real-time",
      label: "Analysis Speed",
      icon: "fas fa-bolt"
    },
    {
      stat: "99.5%",
      label: "Answer Accuracy",
      icon: "fas fa-bullseye"
    },
    {
      stat: "24/7",
      label: "AI Availability",
      icon: "fas fa-clock"
    }
  ];

  return (
    <div className="ai-technology-showcase py-5" style={{ background: '#f8f9fa' }}>
      <div className="container py-5">
        
        {/* Section Header */}
        <div className="row align-items-center mb-5 pb-4">
          <div className="col-lg-6 mb-4 mb-lg-0">
            <div className="d-inline-flex align-items-center px-4 py-2 mb-3 rounded-pill"
              style={{
                background: 'linear-gradient(135deg, #667eea15, #764ba215)',
                border: '1px solid #667eea30'
              }}>
              <i className="fas fa-robot me-2 text-primary"></i>
              <span className="fw-semibold text-primary">AI Technology</span>
            </div>
            <h2 className="display-4 fw-bold mb-3">
              The <span style={{ 
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Brain</span> Behind WiZiAi
            </h2>
            <p className="lead text-muted mb-4">
              Discover the cutting-edge artificial intelligence technology that powers 
              personalized learning experiences and accelerates student success.
            </p>

            {/* AI Capabilities */}
            <div className="row g-3">
              {aiCapabilities.map((cap, index) => (
                <div key={index} className="col-6">
                  <div className="rounded-3 p-3"
                    style={{
                      background: 'white',
                      border: '2px solid #e9ecef',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#667eea';
                      e.currentTarget.style.transform = 'translateY(-5px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e9ecef';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}>
                    <i className={`${cap.icon} fa-2x text-primary mb-2`}></i>
                    <div className="fw-bold fs-4 text-primary">{cap.stat}</div>
                    <small className="text-muted fw-semibold">{cap.label}</small>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="col-lg-6">
            {/* AI Technology Visual */}
            <div className="position-relative">
              <div className="rounded-4 overflow-hidden shadow-lg"
                style={{
                  border: '3px solid #667eea',
                  background: 'linear-gradient(135deg, #667eea10, #764ba210)'
                }}>
                
                {/* AI Technology Image */}
                <img 
                  src="/img/home-v2/ai-technology-showcase.jpg"
                  alt="AI Technology Neural Network"
                  className="w-100"
                  style={{ height: '450px', objectFit: 'cover' }}
                />

                {/* Animated Particles Effect (CSS) */}
                <div className="position-absolute w-100 h-100 top-0 start-0" 
                  style={{ pointerEvents: 'none', opacity: 0.3 }}>
                  <div className="position-absolute rounded-circle"
                    style={{
                      width: '10px',
                      height: '10px',
                      background: '#667eea',
                      top: '20%',
                      left: '15%',
                      animation: 'pulse 3s infinite'
                    }}></div>
                  <div className="position-absolute rounded-circle"
                    style={{
                      width: '8px',
                      height: '8px',
                      background: '#764ba2',
                      top: '60%',
                      right: '20%',
                      animation: 'pulse 4s infinite'
                    }}></div>
                  <div className="position-absolute rounded-circle"
                    style={{
                      width: '12px',
                      height: '12px',
                      background: '#2d9cdb',
                      bottom: '30%',
                      left: '25%',
                      animation: 'pulse 5s infinite'
                    }}></div>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="position-absolute" 
                style={{ top: '-15px', right: '-15px' }}>
                <div className="rounded-3 p-3 shadow-lg"
                  style={{
                    background: 'white',
                    border: '3px solid #667eea'
                  }}>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-robot fa-2x me-2 text-primary"></i>
                    <div>
                      <div className="fw-bold text-primary">Latest AI</div>
                      <small className="text-muted">Powered</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Technology Features Grid */}
        <div className="row g-4 mt-4">
          {techFeatures.map((feature, index) => (
            <div key={index} className="col-lg-4 col-md-6">
              <div className="card h-100 border-0 shadow-sm"
                style={{
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 15px 30px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                }}>
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle me-3"
                      style={{
                        width: '60px',
                        height: '60px',
                        background: `${feature.color}15`,
                        border: `2px solid ${feature.color}`
                      }}>
                      <i className={feature.icon} style={{ color: feature.color, fontSize: '1.5rem' }}></i>
                    </div>
                    <h5 className="mb-0 fw-bold" style={{ color: feature.color }}>
                      {feature.title}
                    </h5>
                  </div>
                  <p className="text-muted mb-0">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* How AI Helps Section */}
        <div className="rounded-4 p-5 mt-5"
          style={{
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            position: 'relative',
            overflow: 'hidden'
          }}>
          <div className="position-relative" style={{ zIndex: 1 }}>
            <div className="row align-items-center g-4">
              <div className="col-lg-8">
                <h3 className="text-white fw-bold mb-3">
                  Why AI Makes a Difference in Your Preparation
                </h3>
                <p className="text-white mb-4" style={{ opacity: 0.95, fontSize: '1.1rem' }}>
                  Traditional preparation methods treat all students the same. Our AI understands 
                  that every student is unique, with different strengths, weaknesses, and learning styles. 
                  It creates a truly personalized experience that evolves with you, ensuring maximum 
                  efficiency and faster results.
                </p>
                <ul className="list-unstyled text-white">
                  <li className="d-flex align-items-center mb-2">
                    <i className="fas fa-check-circle me-2"></i>
                    <span>Identifies knowledge gaps you didn't know you had</span>
                  </li>
                  <li className="d-flex align-items-center mb-2">
                    <i className="fas fa-check-circle me-2"></i>
                    <span>Saves 40% of study time with targeted learning</span>
                  </li>
                  <li className="d-flex align-items-center mb-2">
                    <i className="fas fa-check-circle me-2"></i>
                    <span>Adapts in real-time as your skills improve</span>
                  </li>
                  <li className="d-flex align-items-center mb-2">
                    <i className="fas fa-check-circle me-2"></i>
                    <span>Provides insights that human teachers might miss</span>
                  </li>
                </ul>
              </div>
              <div className="col-lg-4 text-center">
                <Link to="/signup" 
                  className="btn btn-light btn-lg px-5 py-3 rounded-pill shadow-lg">
                  <i className="fas fa-rocket me-2"></i>
                  Experience AI Learning
                </Link>
              </div>
            </div>
          </div>

          {/* Background Decoration */}
          <div className="position-absolute w-100 h-100 top-0 start-0" style={{ opacity: 0.1 }}>
            <div className="position-absolute rounded-circle" 
              style={{ width: '400px', height: '400px', background: 'white', top: '-20%', right: '-10%' }}></div>
            <div className="position-absolute rounded-circle" 
              style={{ width: '300px', height: '300px', background: 'white', bottom: '-15%', left: '-8%' }}></div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 0.5;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.5);
          }
        }
      `}</style>
    </div>
  );
};

export default AITechnologyShowcase;
